/**
 * Управление содержимым модального окна: body scroll, loading/unloading body,
 * mask (overlay spinner), backdrop observer.
 */

/**
 * Пересчитывает максимальную высоту modal-body / offcanvas-body.
 * При пользовательском resize — flexbox сам распределяет высоту.
 * @param {ModalManager} manager
 */
export function setModalBodyScroll(manager) {
    const isCanvas = manager.type === 'canvas';
    const bodySelector = isCanvas ? '.offcanvas-body' : '.modal-body';
    const headerSelector = isCanvas ? '.offcanvas-header' : '.modal-header';
    const footerSelector = isCanvas ? '.offcanvas-footer' : '.modal-footer';

    const bodies = manager.modalElement.querySelectorAll(bodySelector);

    if (bodies.length) {
        const content = manager.modalDialog?.querySelector('.modal-content');
        const isResized = !isCanvas && content?.dataset.resized === 'true';

        bodies.forEach((body) => {
            const header = manager.modalElement.querySelector(headerSelector);
            const footer = manager.modalElement.querySelector(footerSelector);

            const headerHeight = header ? header.offsetHeight : 0;
            const footerHeight = footer ? footer.offsetHeight : 0;

            body.style.overflowY = 'auto';

            if (isResized) {
                body.style.maxHeight = '';
                body.style.minHeight = '0';
            } else {
                const modalMargin = isCanvas ? 0 : 90;
                const maxHeight = `calc(100vh - ${headerHeight}px - ${footerHeight}px - ${modalMargin}px)`;
                body.style.maxHeight = maxHeight;
            }
        });
    }
}

/**
 * Создаёт маску-оверлей с spinner.
 * @param {ModalManager} manager
 */
export function createMask(manager) {
    const container = document.querySelector('.fullScreenElement, body');
    manager.mask = document.createElement('div');
    manager.mask.classList.add('modalMask');

    const spinner = document.createElement('div');
    spinner.classList.add('spinner-border', 'text-primary');
    spinner.setAttribute('role', 'status');

    const srOnly = document.createElement('span');
    srOnly.classList.add('sr-only');
    srOnly.textContent = 'Loading...';

    spinner.appendChild(srOnly);
    manager.mask.appendChild(spinner);

    if (container) container.append(manager.mask);
}

/**
 * Удаляет маску-оверлей.
 * @param {ModalManager} manager
 */
export function removeMask(manager) {
    if (manager.mask) manager.mask.remove();
}

/**
 * Показывает placeholder-загрузку внутри modal-body.
 * @param {ModalManager} manager
 */
export function loadBody(manager) {
    const bodySelector = manager.type === 'canvas' ? '.offcanvas-body' : '.modal-body';
    const modalBody = manager.modalElement.querySelector(bodySelector);
    if (modalBody && manager._loadBody) {
        modalBody.classList.add('placeholder-glow');

        const allElements = modalBody.querySelectorAll('*');
        let hasContent = false;

        allElements.forEach((element) => {
            if (element.children.length === 0) {
                element.classList.add('placeholder');
            } else {
                hasContent = true;
            }
        });

        if (!hasContent) {
            const img = document.createElement('img');
            img.src = '/assets/img/loading-loading-forever.gif';
            img.className = 'modal-loading-image';
            modalBody.appendChild(img);
        }
    } else {
        if (manager._loadBody) console.error('Body not found');
    }
}

/**
 * Восстанавливает оригинальное содержимое modal-body.
 * @param {ModalManager} manager
 */
export function unLoadBody(manager) {
    const bodySelector = manager.type === 'canvas' ? '.offcanvas-body' : '.modal-body';
    const modalBody = manager.modalElement.querySelector(bodySelector);
    if (modalBody && manager.originalContent) {
        modalBody.innerHTML = manager.originalContent;
        modalBody.classList.remove('modal-loading');
    } else {
        console.error('Original content not found or body not found');
    }
}

/**
 * Следит за появлением .modal-backdrop/.offcanvas-backdrop
 * и перемещает в .fullScreenElement.
 * @param {ModalManager} manager
 */
export function initBackdropObserver(manager) {
    const fullScreenElement = document.querySelector('.fullScreenElement');
    if (!fullScreenElement) return;

    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement &&
                    (node.classList.contains('modal-backdrop') || node.classList.contains('offcanvas-backdrop'))) {
                    fullScreenElement.appendChild(node);
                }
            });
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: false
    });

    if (manager.modalElement) {
        const hiddenEvent = manager.type === 'canvas' ? 'hidden.bs.offcanvas' : 'hidden.bs.modal';
        manager.modalElement.addEventListener(hiddenEvent, () => {
            requestAnimationFrame(() => {
                fullScreenElement.focus();
            });
        });
    }
}
